/**
 * Client wrapper for the signed-next OAuth redirect (S3.4, plan §4.6).
 *
 * Flow:
 *   1. ProtectedRoute / OnboardingRoute caught an unauthenticated user.
 *      It writes the user's intended destination to sessionStorage with
 *      `rememberPendingDestination()` (sync, runs during render).
 *   2. User clicks "Sign in with Google" on Home or SignInButton. The
 *      click handler awaits `prepareAuthNextCookie()`, which POSTs the
 *      pending destination to /api/auth/sign-next. The server validates,
 *      signs a JWT, and sets an HttpOnly cookie.
 *   3. supabase.auth.signInWithOAuth() fires. The cookie rides through
 *      Google's redirect back to /auth/callback (SameSite=Lax).
 *   4. /auth/callback POSTs to /api/auth/post-callback. The server verifies
 *      the cookie, atomically consumes the jti, returns the destination.
 *      Client navigates there. extension-auth still uses its own marker.
 *
 * Tab race: two tabs sharing the same cookie can both reach post-callback.
 * The auth_next_seen PK on jti means the first INSERT wins, second returns
 * 23505 and gets redirect_to=null (falls back to /saved). Acceptable.
 */

const PENDING_KEY = 'solem_pending_destination';

export function rememberPendingDestination(destination: string): void {
  if (typeof window === 'undefined') return;
  if (!destination || destination.length > 2048) return;
  try {
    sessionStorage.setItem(PENDING_KEY, destination);
  } catch {
    // sessionStorage can throw in private mode quota-exceeded; non-fatal.
  }
}

export function readPendingDestination(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(PENDING_KEY);
  } catch {
    return null;
  }
}

export function clearPendingDestination(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(PENDING_KEY);
  } catch {
    // ignore
  }
}

/**
 * Read a pending destination AND validate it's a local same-origin path.
 * Defense in depth — the server already validated when signing/verifying
 * the JWT, but a user could manually edit sessionStorage between writes.
 * Returns null if missing, malformed, or escaping single-origin boundary.
 *
 * Does NOT clear sessionStorage — caller decides via consumePendingDestination.
 */
export function safePendingDestination(): string | null {
  const raw = readPendingDestination();
  if (!raw) return null;
  if (raw.length === 0 || raw.length > 2048) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//')) return null; // protocol-relative
  if (/[\x00-\x1F\x7F]/.test(raw)) return null; // control chars
  if (raw.includes('\\')) return null;
  // Block scheme prefixes that could be smuggled (e.g. "/javascript:..." won't
  // start with a colon at index 0, but be defensive about any colon before
  // the first '/' segment past the leading slash).
  if (/^\/[^/]*:/.test(raw)) return null;
  return raw;
}

/**
 * Atomic-style helper: read the validated destination AND clear sessionStorage
 * in one call. Use this in onboarding-complete handlers to prevent the marker
 * from leaking into subsequent sign-ins.
 */
export function consumePendingDestination(): string | null {
  const dest = safePendingDestination();
  clearPendingDestination();
  return dest;
}

/**
 * If a pending destination is queued, POST it to /api/auth/sign-next so the
 * server sets the HttpOnly cookie before OAuth fires. Always resolves, never
 * throws — sign-next failure just means the user lands on /saved after auth,
 * which is the safe default.
 *
 * Returns true if a cookie was successfully set, false otherwise. Callers
 * don't need the return value to function correctly; it's for telemetry.
 */
export async function prepareAuthNextCookie(): Promise<boolean> {
  const destination = readPendingDestination();

  // Always call sign-next, even when there's no pending destination. An
  // abandoned/canceled prior OAuth attempt can leave a stale solem_auth_next
  // cookie on the browser; if we don't make this call, the next sign-in
  // would consume that stale cookie and hijack the user's destination.
  // Posting with destination: null tells the server to clear the cookie.
  try {
    const res = await fetch('/api/auth/sign-next', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ destination: destination ?? null }),
    });
    if (!res.ok) return false;
    const data = await res.json().catch(() => ({ signed: false }));
    return Boolean(data?.signed);
  } catch {
    return false;
  } finally {
    // Clear the sessionStorage marker. The cookie is now source of truth.
    clearPendingDestination();
  }
}
